import { lazy, Suspense, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Users, BedDouble, Ruler, MapPin, LogIn, LogOut } from 'lucide-react';
import type { PublicApartment } from '@/api/generated';
import { Photo } from '@/components/ui/photo';
import { PhotoCarousel } from '@/components/PhotoCarousel';
import { HouseRulesAccordion } from '@/components/HouseRulesAccordion';
import { formatPriceMajor } from '@/lib/money';
import { ogCardSrc } from '@/lib/img';

// Lazy-load the booking widget (quote/reserve flow + its DateRangePicker) so
// none of it is hoisted into the entry bundle that loads on every page —
// only fetched once an apartment-detail page actually mounts. react-day-picker
// itself (the heaviest piece, ~70 KB) gets its OWN async chunk one level
// deeper: DateRangePicker.tsx lazy-loads DateRangeCalendar so the library is
// deferred until a calendar panel actually opens, not merely until this
// widget mounts — that boundary is what keeps the home route's
// `presentation="field"` picker (which imports DateRangePicker directly,
// never through this lazy()) out of the entry chunk too (OH-64 whole-branch
// review, CRITICAL 1).
const BookingWidget = lazy(() =>
  import('@/components/BookingWidget').then((m) => ({ default: m.BookingWidget })),
);
import {
  apartmentQuery,
  coverPhoto,
  orderedPhotos,
  formatAmenity,
  resolveHouseRules,
  HttpError,
} from '@/lib/apartments';
import { applySeo, apartmentJsonLd } from '@/lib/seo';
import { captureApartmentView } from '@/lib/analytics';
import { normalizedPartyFromSearch, partyToSearch, type PartySearchParams } from '@/lib/party';

// Narrow an unknown query-string value the way search.tsx does — a repeated
// `child_age` decodes to an array, a lone one to a bare value.
function numberish(v: unknown): number | string | undefined {
  return typeof v === 'number' || typeof v === 'string' ? v : undefined;
}
function childAgeish(v: unknown): number | string | (number | string)[] | undefined {
  if (Array.isArray(v)) {
    return v.filter((x): x is number | string => typeof x === 'number' || typeof x === 'string');
  }
  return numberish(v);
}
import { isValidIsoDate } from '@/lib/booking';
import { m } from '@/paraglide/messages';

export const Route = createFileRoute('/apartment/$id')({
  // Dates in the URL make the page shareable ("this flat, these nights") and
  // keep a single source of truth: the booking widget derives its range
  // from here rather than holding a copy that Back-navigation would
  // desynchronise from the address bar.
  //
  // isValidIsoDate, not just `typeof === 'string'` (OH-64 whole-branch
  // review, CRITICAL 2): any string used to pass through unvalidated
  // straight to DateRangeCalendar's parseIso, which THROWS on anything that
  // doesn't split into three numeric parts — during render, bubbling to the
  // root errorComponent and blanking the whole page on a truncated or
  // hand-edited link (e.g. `?checkin=2026-08`). A structurally-shaped but
  // impossible date (`2026-02-31`) used to sail through as three valid
  // numbers, silently producing "Invalid Date" → NaN nights → an enabled
  // "See price" that would have fired a NaN-NaN-NaN request. Each field is
  // validated independently: a well-formed LONE checkout (no checkin) is
  // NOT dropped here — the reducer already treats that combination as "no
  // selection" (dateRangeState.ts's `set` branch), which is existing
  // behaviour this fix does not touch.
  validateSearch: (
    s: Record<string, unknown>,
  ): { checkin?: string; checkout?: string; guests?: number } & PartySearchParams => {
    const checkin = typeof s.checkin === 'string' && isValidIsoDate(s.checkin) ? s.checkin : undefined;
    const checkout = typeof s.checkout === 'string' && isValidIsoDate(s.checkout) ? s.checkout : undefined;

    // The party the guest chose in search, carried here by ApartmentCard so
    // the booking widget opens on their answer instead of resetting to a
    // default and reserving for a party nobody picked.
    //
    // Absence stays absence: with no party in the URL this must NOT invent
    // one. partyFromSearch floors adults at 1 — right for defending against
    // a garbled URL, wrong here, because a fabricated `adults: 1` is
    // indistinguishable from a guest who really said one, and would stop the
    // widget applying its own "no information yet" default of 2.
    const stated =
      s.adults !== undefined || s.child_age !== undefined || s.infants !== undefined || s.guests !== undefined;
    if (!stated) {
      return { checkin, checkout, adults: undefined, child_age: undefined, infants: undefined, guests: undefined };
    }
    const party = partyToSearch(
      normalizedPartyFromSearch({
        adults: numberish(s.adults),
        child_age: childAgeish(s.child_age),
        infants: numberish(s.infants),
        guests: numberish(s.guests),
      }),
    );
    // Every key named, including the cleared ones: the router merges the raw
    // search back over this result, so an omitted key leaves a hand-typed
    // value standing (see search.tsx for the same rule and why).
    return {
      checkin,
      checkout,
      adults: party.adults,
      child_age: party.child_age,
      infants: party.infants,
      guests: undefined,
    };
  },
  component: ApartmentPage,
});

function ApartmentPage() {
  const { id } = Route.useParams();
  const { data, isPending, isError, error, refetch } = useQuery(
    apartmentQuery(id),
  );

  const notFound = isError && error instanceof HttpError && error.status === 404;

  return (
    <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-muted transition hover:text-[var(--ollio-navy)]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {m.apartment_back()}
      </Link>

      {isPending ? (
        <ApartmentSkeleton />
      ) : notFound ? (
        <StatusBlock
          title={m.apartment_not_found_title()}
          body={m.apartment_not_found_body()}
        />
      ) : isError ? (
        <StatusBlock title={m.apartment_not_found_title()} body={m.apartment_error()}>
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex items-center justify-center rounded-[var(--radius-control)] bg-primary px-4 py-2 font-semibold text-primary-foreground transition hover:opacity-90"
          >
            {m.home_retry()}
          </button>
        </StatusBlock>
      ) : (
        <ApartmentDetailContent apt={data} />
      )}
    </main>
  );
}

export function ApartmentDetailContent({ apt }: { apt: PublicApartment }) {
  // apt.from_price_minor's true wire type is `number | null | undefined`
  // (nullable: true, api/booking-openapi.yaml) even though the generated
  // PublicApartment DTO widens it to `number | undefined` — @hey-api drops
  // the null arm from a nullable OPTIONAL field (same gap `city` has on
  // this DTO). The generated fetch client is a plain JSON.parse with no
  // coercion layer (src/api/generated/client/client.gen.ts), so a server
  // `null` really does arrive as JS `null` here, distinct from an absent
  // key. Widen the local binding back to the honest type once, so the
  // three states below compare against a type that admits all three,
  // rather than comparing `=== null` against a declared type that (falsely)
  // claims it never is.
  const fromPriceMinor: number | null | undefined = apt.from_price_minor;

  // OH-69 §5.7/§7 criterion 8: absent/null/whitespace-only all collapse to
  // `null` — an apartment with no house rules renders no block at all.
  const houseRules = resolveHouseRules(apt);

  useEffect(() => {
    const cover = coverPhoto(apt);
    const url = typeof window !== 'undefined' ? window.location.href : '';
    captureApartmentView(apt.id);
    return applySeo({
      title: `${apt.title} — ollio`,
      description: apt.description ?? undefined,
      // Card-shaped (1200x630 cover crop), not the raw original: the Function
      // that serves scrapers already sends the card via the same helper, and
      // the client must not overwrite that card with an uncropped image once
      // the SPA hydrates and applySeo re-runs.
      image: cover ? ogCardSrc(cover.url) : undefined,
      url,
      jsonLd: apartmentJsonLd(apt, url),
    });
  }, [apt]);

  return (
    <article className="space-y-8 pb-24 lg:pb-0">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl font-semibold leading-[1.1] tracking-tight text-[var(--ollio-ink)] sm:text-[2.75rem]">
          {apt.title}
        </h1>
        {apt.city ? (
          <p className="flex items-center gap-1.5 text-muted">
            <MapPin className="h-4 w-4" aria-hidden="true" />
            {apt.city}
          </p>
        ) : null}
        {/* Price caption: read before the guest scrolls to the calendar
            (OH-63 Task 4). Placed at the top of the page rather than beside
            the booking widget so it's visible immediately on both viewports
            — on mobile the two-column layout stacks the details column
            (stats/description/amenities) ABOVE the widget's <aside>, which
            would push a caption living there far down the page. The
            calendar underneath stays functional either way: a sold-out
            apartment may still have free dates past the 90-day window, and
            the picker (further down, inside BookingWidget) shows them
            regardless of this caption.
            Three states, same rule ApartmentCard uses (whole-branch review
            IMPORTANT 1): a number is the from-price; an explicit null means
            the server confirmed no sellable night ("no free dates"); an
            absent/undefined field means the server couldn't tell (lookup
            error, or a deploy-skew window where this client is newer than
            the API) and renders no caption at all rather than a false
            "Sold out". */}
        {typeof fromPriceMinor === 'number' ? (
          <p className="text-base font-medium text-[var(--ollio-gold-deep)]">
            {m.apartment_from_price({
              price: formatPriceMajor(fromPriceMinor / 100, apt.currency),
              guests: apt.occupancy_max,
            })}
          </p>
        ) : fromPriceMinor === null ? (
          <p className="text-base text-muted">{m.apartment_sold_out()}</p>
        ) : null}
      </header>

      <Gallery apt={apt} />

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_380px] lg:items-start">
        {/* Left column: details */}
        <div className="space-y-8">
          <section aria-label={m.apartment_details_heading()}>
            <h2 className="sr-only">{m.apartment_details_heading()}</h2>
            <dl className="flex flex-wrap gap-x-10 gap-y-5 border-b border-[var(--border-hairline)] pb-7">
              <Stat
                icon={<Users className="h-[22px] w-[22px]" />}
                label={m.apartment_detail_guests({ count: apt.occupancy_max })}
              />
              {typeof apt.bedrooms === 'number' ? (
                <Stat
                  icon={<BedDouble className="h-[22px] w-[22px]" />}
                  label={`${apt.bedrooms} ${apt.bedrooms === 1 ? 'bedroom' : 'bedrooms'}`}
                />
              ) : null}
              {typeof apt.area_sqm === 'number' ? (
                <Stat
                  icon={<Ruler className="h-[22px] w-[22px]" />}
                  label={m.apartment_detail_area({ area: apt.area_sqm })}
                />
              ) : null}
            </dl>
          </section>

          {apt.description ? (
            <section>
              <h2 className="mb-3 font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
                {m.apartment_about_heading()}
              </h2>
              <p className="whitespace-pre-line text-pretty leading-relaxed text-[var(--text-body)]">
                {apt.description}
              </p>
            </section>
          ) : null}

          {apt.amenities.length > 0 ? (
            <section>
              <h2 className="mb-4 font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
                {m.apartment_amenities_heading()}
              </h2>
              <ul className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {apt.amenities.map((a) => (
                  <li
                    key={a}
                    className="flex items-center gap-3 text-sm text-[var(--text-body)]"
                  >
                    <span
                      className="h-1.5 w-1.5 flex-none rotate-45 bg-[var(--ollio-gold)]"
                      aria-hidden="true"
                    />
                    {formatAmenity(a)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Arrival and departure times. They were on the FAQ and in the
              Terms and nowhere near the point where a guest is working out
              whether a flight lands in time — which is the moment the answer
              matters. Per-apartment rather than a site-wide constant because
              that is where the data lives; all seven agree today, and this
              keeps telling the truth on the day one of them does not.

              Rendered only when BOTH are present: half of an arrival window
              is not information, it is a new question. */}
          {apt.check_in_from && apt.check_out_until ? (
            <section>
              <h2 className="mb-4 font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
                {m.apartment_times_heading()}
              </h2>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 text-sm text-[var(--text-body)]">
                  <LogIn className="h-4 w-4 flex-none text-[var(--ollio-gold)]" aria-hidden="true" />
                  <dt className="sr-only">Check-in</dt>
                  <dd>{m.apartment_checkin_from({ time: apt.check_in_from })}</dd>
                </div>
                <div className="flex items-center gap-3 text-sm text-[var(--text-body)]">
                  <LogOut className="h-4 w-4 flex-none text-[var(--ollio-gold)]" aria-hidden="true" />
                  <dt className="sr-only">Check-out</dt>
                  <dd>{m.apartment_checkout_until({ time: apt.check_out_until })}</dd>
                </div>
              </dl>
              <p className="mt-3 text-sm text-muted">{m.apartment_times_note()}</p>
            </section>
          ) : null}

          {houseRules ? <HouseRulesAccordion text={houseRules} /> : null}
        </div>

        {/* Right column: sticky booking widget (stacks below on mobile).
            id="book" is the scroll target for the mobile sticky CTA below. */}
        <aside id="book" className="scroll-mt-20 lg:sticky lg:top-24">
          <Suspense fallback={<BookingWidgetFallback />}>
            <BookingWidget apt={apt} />
          </Suspense>
        </aside>
      </div>

      {/* Mobile-only sticky CTA — the booking widget is far down the stacked
          mobile layout, so keep a persistent "check availability" affordance
          that scrolls to it. Hidden on lg where the widget is sticky-visible.

          `bottom` follows the consent banner (OH-72), which is fixed to the
          same edge one z-layer above and would otherwise bury this button for
          every first-time phone visitor — the pending state being where all
          real traffic starts. ConsentBanner publishes its measured height; the
          fallback of 0px is what applies once a choice exists. */}
      <div className="fixed inset-x-0 bottom-[var(--consent-banner-height,0px)] z-30 border-t border-[var(--border-hairline)] bg-[rgba(250,250,248,0.96)] px-6 py-3 backdrop-blur-[10px] lg:hidden">
        <button
          type="button"
          onClick={() =>
            document.getElementById('book')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          className="inline-flex h-11 w-full items-center justify-center rounded-full bg-accent font-semibold text-accent-foreground shadow-[var(--shadow-gold)] transition hover:bg-[var(--accent-hover)]"
        >
          Check availability
        </button>
      </div>
    </article>
  );
}

function Gallery({ apt }: { apt: PublicApartment }) {
  const ordered = orderedPhotos(apt);

  if (ordered.length === 0) {
    return (
      <div className="aspect-[16/9] w-full overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)]">
        <Photo seed={apt.title.length} label={apt.title} />
      </div>
    );
  }

  return <PhotoCarousel photos={ordered} title={apt.title} />;
}

function BookingWidgetFallback() {
  return (
    <div
      className="h-[440px] animate-pulse rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card shadow-[var(--shadow-lg)]"
      aria-hidden="true"
    />
  );
}

function Stat({ icon, label }: { icon: React.ReactNode; label: string }) {
  // Vertical: icon above label (matches the design's stat row).
  return (
    <div className="flex flex-col items-start gap-2">
      <span className="text-[var(--ollio-navy)]" aria-hidden="true">
        {icon}
      </span>
      <dd className="text-sm font-medium text-[var(--text-body)]">{label}</dd>
    </div>
  );
}

function StatusBlock({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="grid place-items-center rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-6 py-16 text-center shadow-[var(--shadow-sm)]">
      <h1 className="mb-2 font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
        {title}
      </h1>
      <p className="mb-6 max-w-md text-muted">{body}</p>
      {children}
    </div>
  );
}

function ApartmentSkeleton() {
  return (
    <div className="space-y-8" aria-busy="true" aria-label={m.apartment_loading()}>
      <div className="space-y-3">
        <div className="h-9 w-1/2 animate-pulse rounded bg-[var(--surface-sunken)]" />
        <div className="h-4 w-1/4 animate-pulse rounded bg-[var(--surface-sunken)]" />
      </div>
      <div className="aspect-[16/9] w-full animate-pulse rounded-[var(--radius-card)] bg-[var(--surface-sunken)]" />
      <div className="h-24 w-full animate-pulse rounded bg-[var(--surface-sunken)]" />
    </div>
  );
}
